// utils/imageResize.js
// Shrinks a camera photo before it enters any upload queue, so the web queue,
// the service worker and the native uploader all send the small file. The
// tablet camera hands over the full sensor image (about 12 MP, several MB);
// that is what saturated the factory wifi. Size and JPEG quality are set per
// factory (see syncWindow.js); these are the defaults.

export const DEFAULT_PHOTO_MAX_SIDE = 1600;
export const DEFAULT_PHOTO_JPEG_QUALITY = 0.72;

// Fit inside maxSide on the longest side, keeping the aspect ratio. Never
// enlarges: a photo that is already small enough is left alone.
export function computeTargetSize(width, height, maxSide) {
  const longest = Math.max(width, height);
  if (!maxSide || !longest || longest <= maxSide) return { width, height, resized: false };
  const scale = maxSide / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    resized: true,
  };
}

// Decode with the EXIF orientation applied: the canvas drops EXIF, so the
// pixels themselves have to be upright.
async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Older WebView without the option: fall through to <img>.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('image decode failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function encodeJpeg(image, { width, height }, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

const browserCodec = { decode: decodeImage, encode: encodeJpeg };

/**
 * Returns a smaller JPEG Blob, or the original file when shrinking is not
 * possible or not worth it (already small, decode failed, result not smaller).
 * Never throws: losing a shot is worse than uploading a large one.
 */
export async function shrinkPhoto(file, settings = {}, codec = browserCodec) {
  if (!file) return file;
  const maxSide = Number(settings?.photo_max_side) || DEFAULT_PHOTO_MAX_SIDE;
  const quality = Number(settings?.photo_jpeg_quality) || DEFAULT_PHOTO_JPEG_QUALITY;
  try {
    const image = await codec.decode(file);
    try {
      const target = computeTargetSize(image.width, image.height, maxSide);
      if (!target.resized) return file;
      const blob = await codec.encode(image, target, quality);
      return blob && blob.size > 0 && blob.size < file.size ? blob : file;
    } finally {
      image.close?.();
    }
  } catch {
    return file;
  }
}
